import { UnitApi } from '@/api';

// Command Types
export type InputFieldType = 'text' | 'select' | 'date' | 'number' | 'checkbox';

export interface InputFieldDefinition {
  id: string;
  label: string;
  type: InputFieldType;
  required?: boolean;
  options?: { label: string; value: string | number; description: string }[]; // For select fields
  placeholder?: string;
  defaultValue?: any;
  validation?: (value: any) => string | null; // Returns error message or null if valid
  multiSelect?: boolean; // Add support for multi-select fields
}

export interface CommandDefinition {
  id: string;
  label: string;
  description: string;
  inputs: InputFieldDefinition[];
  execute: (inputValues: Record<string, any>) => Promise<CommandResponse>;
}

export interface CommandResponse {
  success: boolean;
  message: string;
  data?: any;
}

// Sample commands
export const updateUnitStatusCommand: CommandDefinition = {
  id: 'UPDATE-UNIT-STATUS',
  label: 'Update Unit Status',
  description: 'Change the status of one or more units',
  inputs: [
    {
      id: 'unitId',
      label: 'Units',
      type: 'select',
      required: true,
      options: [],
      multiSelect: true, // Enable multiple units selection
    },
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: [],
    },
  ],
  execute: async (values) => {
    try {
      const unitIds = Array.isArray(values.unitId)
        ? values.unitId
        : [values.unitId];
      const payload = {
        customStatusId: values.status === 'none' ? null : values.status,
      };

      // Process each unit
      const promises = unitIds.map((unitId) =>
        UnitApi.setUnitShiftCustomStatus(unitId, payload),
      );

      // Wait for all status updates to complete
      await Promise.all(promises);

      return {
        success: true,
        message: `Status updated successfully for ${unitIds.length} unit(s).`,
        data: values,
      };
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || 'An unknown error occurred';
      return {
        success: false,
        message: errorMessage,
        data: null,
      };
    }
  },
};

// Export all commands
export { createGoToCommand } from './commands/GoToCommand';
export { createUpdateUnitShiftStatusCommand } from './commands/UpdateUnitShiftStatusCommand';
